// src/typeof/typeof.ts
var typeOf = (value) => Object.prototype.toString.call(value).slice(8, -1).toLowerCase();
var isArray = (value) => typeOf(value) === "array" /* Array */;
var isFunction = (value) => typeOf(value).includes("function" /* Function */);
var isNull = (value) => typeOf(value) === "null" /* Null */;
var isNumber = (value) => typeOf(value) === "number" /* Number */;
var isObject = (value) => typeOf(value) === "object" /* Object */;
var isPromise = (value) => typeOf(value) === "promise" /* Promise */;
var isRegexp = (value) => typeOf(value) === "regexp" /* Regexp */;
var isString = (value) => typeOf(value) === "string" /* String */;
var isSymbol = (value) => typeOf(value) === "symbol" /* Symbol */;
var isUndefined = (value) => typeOf(value) === "undefined" /* Undefined */;
var isNil = (value) => value == void 0;

// src/create-emitter/create-emitter.ts
function createEmitter(config) {
  let enabled = true;
  let flushing = false;
  let initialized = false;
  const queue = [];
  const subscriptions = {};
  async function dequeue() {
    if (queue.length === 0) {
      flushing = false;
      return;
    }
    const fn = queue.shift();
    await fn?.();
    dequeue();
  }
  function createEnqueuedFn(key) {
    return async function enqueue(...args) {
      return new Promise((resolve, reject) => {
        async function settle() {
          try {
            const fn = config[key];
            const result = await fn?.(...args);
            resolve(result);
            if (!enabled) {
              return;
            }
            for (const symbol of Object.getOwnPropertySymbols(subscriptions)) {
              const subscription = subscriptions[symbol];
              await Promise.all([
                subscription.all?.(key, result),
                subscription[key]?.(result)
              ]);
            }
          } catch (error) {
            reject(error);
          }
        }
        if (key === "initialize") {
          if (initialized) {
            reject(new Error("initialize() can only be called once."));
            return;
          } else {
            initialized = true;
            queue.unshift(settle);
          }
        } else {
          if (isUndefined(config.initialize)) {
            initialized = true;
          }
          queue.push(settle);
        }
        if (!initialized || flushing) {
          return;
        }
        flushing = true;
        dequeue();
      });
    };
  }
  const methods = Object.keys(config).reduce(
    (accumulator, key) => ({
      ...accumulator,
      [key]: createEnqueuedFn(key)
    }),
    {}
  );
  return {
    __SUBSCRIPTIONS__: subscriptions,
    ...methods,
    initialized() {
      return initialized;
    },
    disable() {
      enabled = false;
    },
    enable() {
      enabled = true;
    },
    subscribe(subscription) {
      const key = Symbol(Date.now());
      subscriptions[key] = subscription;
      return function unsubscribe() {
        if (flushing) {
          queue.push(async () => {
            delete subscriptions[key];
          });
          return;
        }
        delete subscriptions[key];
      };
    }
  };
}

// src/create-emitter/types.ts
var types_exports = {};

// src/create-logger/types.ts
var Level = /* @__PURE__ */ ((Level2) => {
  Level2["Error"] = "error";
  Level2["Info"] = "info";
  Level2["Log"] = "log";
  Level2["Warn"] = "warn";
  return Level2;
})(Level || {});

// src/create-logger/create-logger.ts
function createLogger({ enabled, namespace = "" } = {}) {
  function createLogFn(type) {
    return async function logFn(message, { tags = [], ...data } = {}) {
      return {
        data,
        message,
        tags: namespace ? [namespace, ...tags] : tags,
        timestamp: new Date().toISOString(),
        type
      };
    };
  }
  const logger = createEmitter({
    error: createLogFn("error" /* Error */),
    info: createLogFn("info" /* Info */),
    log: createLogFn("log" /* Log */),
    warn: createLogFn("warn" /* Warn */)
  });
  if (enabled === false) {
    logger.disable();
  }
  logger.subscribe({
    all(key, log) {
      if (isUndefined(globalThis?.window))
        return;
      if (!isUndefined(process) && process.env?.NODE_ENV === "production") {
        if (key === "error") {
          console.error(log);
        }
        return;
      }
      const color = {
        ["error" /* Error */]: {
          backgroundColor: "#F94E5C",
          borderColor: "#E22C3A",
          color: "#FFFFFF"
        },
        ["info" /* Info */]: {
          backgroundColor: "#5FBBF0",
          borderColor: "#3CA2DD",
          color: "#FFFFFF"
        },
        ["log" /* Log */]: {
          backgroundColor: "#96979F",
          borderColor: "#717277",
          color: "#FFFFFF"
        },
        ["warn" /* Warn */]: {
          backgroundColor: "#FDDF96",
          borderColor: "#FDCC5D",
          color: "#55565B"
        }
      }[log.type];
      const styles = `
        background-color: ${color.backgroundColor};
        border-radius: 2px;
        border: 1px solid ${color.borderColor};
        color: ${color.color};
        padding: 3px 6px;
      `;
      const title = `${log.type}${namespace ? `:${namespace}` : ""}`;
      console.groupCollapsed(`%c${title}%c ${log.message}`, styles, "");
      console.groupCollapsed("Data");
      console.log(log.data);
      console.groupEnd();
      console.groupCollapsed("Log");
      console.log(log);
      console.groupEnd();
      console.groupCollapsed("Trace");
      console.trace();
      console.groupEnd();
      console.groupEnd();
    }
  });
  logger.subscribe({
    async all(key, log) {
      if (!isUndefined(globalThis?.window))
        return;
      if (!isUndefined(process) && process.env?.NODE_ENV === "production") {
        if (key === "error") {
          console.error(log);
        }
        return;
      }
      const { default: colors } = await import("ansi-colors");
      const color = {
        ["error" /* Error */]: colors.red,
        ["info" /* Info */]: colors.blue,
        ["log" /* Log */]: colors.gray,
        ["warn" /* Warn */]: colors.yellow
      }[log.type];
      const title = `${log.type}${namespace ? `:${namespace}` : ""}`;
      console[log.type](
        color.inverse(` ${title} `),
        color(`${log.message} ${JSON.stringify(log, null, 2)} 
`)
      );
    }
  });
  return logger;
}

// src/guards/guards.ts
var isFalsy = (value) => !value;
var isEmptyCollection = (value) => (Array.isArray(value) || value === Object(value)) && !(value instanceof Date) && !(value instanceof Set) && !(value instanceof Map) && Object.keys(value).length === 0;
var isWhitespaceString = (value) => typeof value === "string" && /^\s*$/.test(value);
var isZeroLengthString = (value) => typeof value === "string" && value.length === 0;
var isInvalidDate = (value) => value instanceof Date && Number.isNaN(value.getTime());
var isEmptySet = (value) => value instanceof Set && value.size === 0;
var isEmptyMap = (value) => value instanceof Map && value.size === 0;
var isBlank = (value) => {
  if (isFalsy(value))
    return true;
  if (isWhitespaceString(value))
    return true;
  if (isEmptySet(value))
    return true;
  if (isEmptyMap(value))
    return true;
  if (isInvalidDate(value))
    return true;
  return isEmptyCollection(value);
};
var isNotBlank = (value) => {
  return !isFalsy(value) && !isWhitespaceString(value) && !isEmptySet(value) && !isEmptyMap(value) && !isInvalidDate(value) && !isEmptyCollection(value);
};

// src/load-script/load-script.ts
function loadScript(source, options) {
  return new Promise((resolve, reject) => {
    if (isUndefined(globalThis?.document)) {
      reject(new Error("Scripts can only be loaded within a browser context."));
    }
    const script = document.createElement("script");
    script.setAttribute("src", source);
    script.setAttribute("type", "text/javascript");
    script.addEventListener("load", () => resolve());
    script.addEventListener("error", (error) => reject(error));
    if (!isUndefined(options)) {
      for (const [key, value] of Object.entries(options)) {
        script.setAttribute(key, String(value));
      }
    }
    document.body.append(script);
  });
}

// src/time/to-hh-mm-ss.ts
function toHHMMSS(time) {
  if (!Number.isFinite(time) || time < 0)
    return null;
  const date = new Date(0);
  date.setSeconds(time);
  return date.toISOString().slice(11, 19).replace(/^00:0?|^0/, "");
}

// src/wait-until/wait-until.ts
function waitUntil(condition, timeout = 1e4) {
  const time = Date.now();
  let id;
  function callback(resolve, reject) {
    if (condition()) {
      window.cancelAnimationFrame(id);
      resolve();
      return;
    }
    if (Date.now() - time >= timeout) {
      window.cancelAnimationFrame(id);
      reject(new Error("Timeout exceeded."));
      return;
    }
    id = window.requestAnimationFrame(() => callback(resolve, reject));
  }
  return new Promise(callback);
}

// src/index.ts
import { default as default2 } from "just-compare";
import { default as default3 } from "just-debounce-it";
import { default as default4 } from "just-extend";
import { default as default5 } from "just-kebab-case";
import { default as default6 } from "just-memoize";
import { default as default7 } from "just-pick";
import { default as default8 } from "just-prune";
import { default as default9 } from "just-safe-set";
import { default as default10 } from "just-shuffle";
import { default as default11 } from "just-split";
import { default as default12 } from "just-throttle";
import { default as default13 } from "just-truncate";
import { useDebounce, useInterval, useLocalStorage, useMediaQuery, useTimeout } from "usehooks-ts";
import { v4 } from "uuid";
export {
  types_exports as CreateEmitter,
  Level,
  default2 as compare,
  createEmitter,
  createLogger,
  default3 as debounce,
  default4 as extend,
  isArray,
  isBlank,
  isEmptyCollection,
  isEmptyMap,
  isEmptySet,
  isFalsy,
  isFunction,
  isInvalidDate,
  isNil,
  isNotBlank,
  isNull,
  isNumber,
  isObject,
  isPromise,
  isRegexp,
  isString,
  isSymbol,
  isUndefined,
  isWhitespaceString,
  isZeroLengthString,
  loadScript,
  default6 as memoize,
  default7 as pick,
  default8 as prune,
  default9 as set,
  default10 as shuffle,
  default5 as slugify,
  default11 as split,
  default12 as throttle,
  toHHMMSS,
  default13 as truncate,
  typeOf,
  useDebounce,
  useInterval,
  useLocalStorage,
  useMediaQuery,
  useTimeout,
  v4 as uuid,
  waitUntil
};
//# sourceMappingURL=index.mjs.map